import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Col,
  Row,
  InputGroup,
  Input,
  CustomInput,
  Form,
  FormGroup,
  Label,
  FormFeedback,
  FormText,
} from 'reactstrap';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Link, Redirect, useHistory } from 'react-router-dom';
import ApolloClient from 'apollo-client';
import AWSAppSyncClient from 'aws-appsync';
import flatten from 'lodash/flatten';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import {
  useAppContext, DeliveryContext,
} from '../../state/GlobalState';
import {
  initialValues, DropDetailsSchema, quantityWarningMsg, ambientWarningMsg,
} from './validation';
import PageHeading from '../../components/PageHeading/PageHeading';
import BackButton from '../../components/BackButton/BackButton';
import { extractAttributeFromCollection } from '../../helpers/attributeExtraction';
import { getShipmentDetails } from '../../api/graphQl/Shipment';
import {
  createDropDetails, getDeliveryDropDetails,
} from '../../api/graphQl/DropDetails';
import withClient from '../../hoc/withClient';
import './_enterDropDetails.scss';
import { skHasValue, generateDropDetailsSK, isUnmappedOpenOrder } from '../../helpers/shipmentEngine';
import LidDetail from '../../components/LidDetail/LidDetail';
import {
  lidAddonClickHandler, productClickHandler, saveClickHandler,
  clearFieldsClickHandler, backButtonClickHandler,
} from '../../analytics/events';
import { getShipmentHeader } from '../../helpers/openOrders';
import { statuses } from '../../helpers/shipmentStatus';
import pageTitleConstant from '../../analytics/constantPageTitle';
import NetworkSpeedChecker from '../../components/NetworkSpeedChecker/NetworkSpeedChecker';

const isAmbientQuantityWarning = (message) => (
  typeof message === 'string' && /discharge quantity cannot be greater than .*quantity ambient/i.test(message)
);

const EnterDropDetails = ({
  graphClient,
}) => {
  const history = useHistory();
  const {
    carrierId, selectedDocket, shipmentId, handleGraphApiError, bpProducts, virtualTerminal, isDPTerminal,
  } = useAppContext();
  const [deliveryId] = useContext(DeliveryContext);
  const [ambientDisable, setambientDisable] = useState(false);
  const [quantityWarning, setQuantityWarning] = useState();
  const [shipmentDetails, setShipmentDetails] = useState([]);
  const [dropDetails, setDropDetails] = useState();
  const [dropDetailsAll, setDropDetailsAll] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState();
  const [selectedLoadIdAddon, setSelectedLoadIdAddon] = useState();
  const [calculatedresult, setCalculatedresult] = useState();
  // useState for Aggregate Drop Details Card
  const [aggregateFlag, setAggregateFlag] = useState(true);
  const [aggregateProduct, setAggregateProduct] = useState('');
  const [aggregateQtyAmbient, setAggregateQtyAmbient] = useState(0);
  const [allLidSelected, setAllLidSelected] = useState(true);
  const [productAmbient, SetProductAmbient] = useState({});
  const [isNumberInputDisabled, setIsNumberInputDisabled] = useState(false);
  const [formReset, setFormReset] = useState(false);  
  const [dropDetailsExists, setDropDetailsExists] = useState(false);
  const formikRef = useRef();

  const normalizedDropDetails = useMemo(() => {
    if (!Array.isArray(dropDetailsAll)) {
      return [];
    }

    const seen = new Set();
    const deduped = [];

    dropDetailsAll.forEach((dropDetail) => {
      const drops = Array.isArray(dropDetail?.DROPS) ? dropDetail.DROPS : [];
      const dropsSignature = drops
        .map((drop) => `${drop?.DropId || ''}|${drop?.Product || ''}|${Number(drop?.ActualDischargeQty) || 0}`)
        .join(',');
      const key = `${dropDetail?.SK || ''}::${dropsSignature}`;

      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(dropDetail);
      }
    });

    return deduped;
  }, [dropDetailsAll]);

  // get lid status from its child component - LidDetails
  const [, setLidStatus] = useState(false);
  const sendDataBackToParent = (valueBack) => {
    setLidStatus(valueBack);
  };
  const [selectedShipmentDetail, setSelectedShipmentDetail] = useState(shipmentDetails.find((sd) => sd.SK.includes(`LID#${selectedLoadIdAddon}`)));
  useEffect(() => {
    if (Array.isArray(selectedLoadIdAddon) && selectedLoadIdAddon.length > 1) {
      setSelectedShipmentDetail(shipmentDetails);
      setAllLidSelected(true);
    } else {
      setSelectedShipmentDetail(shipmentDetails.find((sd) => sd.SK.includes(`LID#${selectedLoadIdAddon}`)));
      setAllLidSelected(false);
    }
  }, [selectedLoadIdAddon, shipmentDetails]);
  const isOpenOrder = isUnmappedOpenOrder(selectedDocket);

  const getSelectedProducts = () => {
    if (!selectedShipmentDetail) return [];

    if (isOpenOrder) return bpProducts;
    if (Array.isArray(selectedShipmentDetail)) {
      return selectedShipmentDetail.map((detail) => detail.Products).flat();
    }
    return selectedShipmentDetail.Products;
  };

  const getSelectedVehicle = (() => {
    if (!selectedShipmentDetail) return '';
    if (Array.isArray(selectedShipmentDetail)) {
      if (Array.isArray(selectedLoadIdAddon) && selectedLoadIdAddon.length > 0) {
        const matchingDetail = selectedShipmentDetail.find((sd) => sd.SK.includes(`LID#${selectedLoadIdAddon[0]}`));
        return matchingDetail?.TASVehicleID || '';
      }
      return selectedShipmentDetail[0]?.TASVehicleID || '';
    }
    return selectedShipmentDetail.TASVehicleID || '';
  })();
  
  if (!shipmentId || !deliveryId) {
    // If there's no shipment id or delivery id, redirect to find shipment page
    return <Redirect to="/shipment" />;
  }

  const setShipmentDetailsForDelivery = (localShipmentDetails) => {
    const shipmentDetailsForDelivery = localShipmentDetails.filter(
      (docket) => skHasValue(docket, deliveryId),
    );
    setShipmentDetails(shipmentDetailsForDelivery);
  };

  const getProducts = (values) => {
    if (isOpenOrder) return bpProducts;

    if (values.LoadIdAddon) {
      const selectedDetail = shipmentDetails.find((sd) => sd.SK.includes(`LID#${values.LoadIdAddon}`));
      return selectedDetail ? selectedDetail.Products : [];
    }
    return [];
  };

  const getLoadIdAddons = () => (shipmentDetails ? extractAttributeFromCollection(shipmentDetails, 'SK', 'LID#') : []);

  const getLoadIdAddonsWithAggregate = () => {
    let lidOptions = [];
    const allLids = [];
    if (shipmentDetails) {
      lidOptions = extractAttributeFromCollection(shipmentDetails, 'SK', 'LID#');
      lidOptions?.forEach((lidOption) => {
        allLids.push(lidOption);
      });
      if (lidOptions?.length > 1) {
        lidOptions.push(allLids);
      }
    }
    return lidOptions;
  };

  if (!selectedLoadIdAddon && getLoadIdAddons().length > 0) {
    setSelectedLoadIdAddon(getLoadIdAddons([0]));
  }

  // unix timestamp in seconds will give a unique value for this use case
  const getDropId = () => Math.floor(Date.now() / 1000);

  // Calculate total delivered amount
  const calculateTotalDeliverd = (dropDetailsAl) => {
    const map = new Map();
    if (dropDetailsAl) {
      dropDetailsAl.forEach((dropDetail) => {
        if (dropDetail.DROPS.length) {
          dropDetail.DROPS.forEach(
            (drop) => {
              const qty = Number(drop.ActualDischargeQty) || 0;
              if (map.has(dropDetail.SK.split('#LID#')[1] + drop.Product)) {
                map.set(
                  dropDetail.SK.split('#LID#')[1] + drop.Product, map.get(dropDetail.SK.split('#LID#')[1] + drop.Product) + qty,
                );
              } else { map.set(dropDetail.SK.split('#LID#')[1] + drop.Product, qty); }
            },
          );
        }
      });
    }
    return map;
  };

  const deliveredTotals = useMemo(
    () => calculateTotalDeliverd(normalizedDropDetails),
    [normalizedDropDetails],
  );

  // Calculate total delivered discharge quantity
  const getTotalDeliverd = (dropDetailsAl) => {
    let totalDischargeQty = 0;
    if (dropDetailsAl) {
      dropDetailsAl.forEach((dropDetail) => {
        if(!dropDetail.SK.includes(',')){
          if (dropDetail.DROPS.length) {
            dropDetail.DROPS.forEach(
              (drop) => {
                totalDischargeQty += Number(drop.ActualDischargeQty) || 0;
              },
            );
          }
        }
      }); 
    }
    return totalDischargeQty;
  };

  // Calculate total delivered amount
  const getProductIdData = async (selectedLoadIdAddon, inputValues, selectedDocket, index=0) => {
    //Adding MultiLoadId field to show in the drop details
    let MultiLoadId='';
    for(const loadIdVal of selectedLoadIdAddon){
      if(loadIdVal.includes(',')){
        MultiLoadId = loadIdVal;
        inputValues = {
          ...inputValues,
          MultiLoadId,
        };
      }
    }
    //////////////////////////////////////////////////////
    let product =  getSelectedProducts()[index];
    if(selectedLoadIdAddon[index]!==undefined){
      let calculateTotalDeliverdVal = deliveredTotals
                                      ? deliveredTotals.get(selectedLoadIdAddon[index] + product.Name)
                                      : 0;
      calculateTotalDeliverdVal = calculateTotalDeliverdVal == undefined ? 0 : calculateTotalDeliverdVal;      
      const remainingAmbient = product.QuantityAmbient - calculateTotalDeliverdVal;
      const shouldIgnoreNegativeRemaining = virtualTerminal && remainingAmbient < 0;
      const remainingAmbientForNextLoad = shouldIgnoreNegativeRemaining
        ? 0
        : remainingAmbient;
      const isLastLoadId = index === getSelectedProducts().length - 1;
      if(inputValues.ActualDischargeQty >= remainingAmbient){
        const dischargeQtyForLoad = virtualTerminal && isLastLoadId
          && inputValues.ActualDischargeQty > remainingAmbient
          ? remainingAmbient + (inputValues.ActualDischargeQty - remainingAmbient)
          : remainingAmbient;
        if((remainingAmbient > 0 || (virtualTerminal && isLastLoadId
          && inputValues.ActualDischargeQty > remainingAmbient))
          && (!virtualTerminal || Number(dischargeQtyForLoad) !== 0)){ // Avoid zero-quantity VT records
          await createDropDetails(graphClient, {
            PK: selectedDocket.PK,
            SK: generateDropDetailsSK(selectedDocket, selectedLoadIdAddon[index]),
            // SK: SK,            
          }, {
            ...inputValues,
            ActualDischargeQty: dischargeQtyForLoad,
          });
        }
          
        index = index + 1;
        if(getSelectedProducts().length>index){
            inputValues = {
              ...inputValues,
              ActualDischargeQty: inputValues.ActualDischargeQty - remainingAmbientForNextLoad, //Acutal quantity if whole quantity is utilized
              
            };
          await getProductIdData(selectedLoadIdAddon, inputValues, selectedDocket, index);
        }
      } else if(inputValues.ActualDischargeQty < (product.QuantityAmbient-calculateTotalDeliverdVal)){
        if (!virtualTerminal || Number(inputValues.ActualDischargeQty) !== 0) {
          await createDropDetails(graphClient, {
            PK: selectedDocket.PK,
            SK: generateDropDetailsSK(selectedDocket, selectedLoadIdAddon[index]),
            // SK: SK,
          }, {
            ...inputValues,
            ActualDischargeQty: inputValues.ActualDischargeQty, // Actual quantity if whole quantity is utilized
          });
        }
      }
    }     
  };

  const calculateSelectedProduct = (dropsDelivered) => {
    if (dropsDelivered && selectedProduct) {
      getSelectedProducts()
        .filter((product) => product.Name === selectedProduct)
        .map((product) => (
          deliveredTotals
            .get(selectedLoadIdAddon + product.Name)
            ? setCalculatedresult((product.QuantityAmbient
            - deliveredTotals
              .get(selectedLoadIdAddon + product.Name)).toFixed(2))
            : setCalculatedresult(product.QuantityAmbient)
        ));
    } else if (getProducts({ LoadIdAddon: selectedLoadIdAddon }).length === 1) {
      getSelectedProducts().filter(
        (product) => product.Name === getProducts({ LoadIdAddon: selectedLoadIdAddon })[0].Name,
      )
        .map((product) => (
          deliveredTotals
            .get(selectedLoadIdAddon + product.Name)
            ? setCalculatedresult((product.QuantityAmbient
                - deliveredTotals
                  .get(selectedLoadIdAddon + product.Name)).toFixed(2))
            : setCalculatedresult(product.QuantityAmbient)
        ));
    } else if (Array.isArray(selectedLoadIdAddon) && selectedLoadIdAddon.length >= 1) {
      setCalculatedresult(aggregateQtyAmbient.toFixed(2))
    } else {
      setCalculatedresult(0);
    }
    return calculatedresult;
  };

  const updateOpenOrderAmbientWarning = (values) => {
    const ambientWarningPrefix = 'Discharge quantity cannot be greater than the quantity ambient (';
    const exceedsAmbient = !virtualTerminal
      && isOpenOrder
      && values.QuantityAmbient !== ''
      && values.QuantityAmbient !== null
      && values.QuantityAmbient !== undefined
      && values.ActualDischargeQty !== ''
      && values.ActualDischargeQty !== null
      && values.ActualDischargeQty !== undefined
      && Number(values.ActualDischargeQty) > Number(values.QuantityAmbient);

    if (exceedsAmbient) {
      setQuantityWarning(`${ambientWarningPrefix}${values.QuantityAmbient}).`);
      setambientDisable(true);
    } else if (quantityWarning?.startsWith(ambientWarningPrefix)) {
      setQuantityWarning();
      setambientDisable(false);
    }
    return exceedsAmbient;
  };

  const ambientValidationCheck = (values) => {
    if (isOpenOrder) {
      return updateOpenOrderAmbientWarning(values);
    }

    if (values.DipBefore?.length !== 0
      && values.DipAfter?.length !== 0
      && normalizedDropDetails
    ) {
      calculateSelectedProduct(normalizedDropDetails);
      if (isNumberInputDisabled) {
        if (values.ActualDischargeQty == values.Calculated) {
          setQuantityWarning();
          setambientDisable(false);

          return;
        }
      }
      if((isDPTerminal) 
          && values.ActualDischargeQty 
          && parseInt(values.Calculated, 10) !== parseInt(values.ActualDischargeQty, 10)
          && parseInt(values.ActualDischargeQty, 10) > parseInt(calculatedresult, 10)){
        setQuantityWarning(virtualTerminalWarningMsg);
      } else if (virtualTerminal && values.ActualDischargeQty) {
        setQuantityWarning();
        setambientDisable(false);
      } else if (!isOpenOrder &&
        values.Calculated && values.ActualDischargeQty
        && normalizedDropDetails
        && parseInt(values.ActualDischargeQty, 10) > parseInt(calculatedresult, 10)
      ) {
        setQuantityWarning(`${ambientWarningMsg}(${calculatedresult}) .`);
        setambientDisable(true);
      } else if (
        !virtualTerminal
        && values.ActualDischargeQty
        && parseInt(values.Calculated, 10) !== parseInt(values.ActualDischargeQty, 10)
      ) {
        setQuantityWarning(quantityWarningMsg);
        setambientDisable(false);
      } else {
        setQuantityWarning();
        setambientDisable(false);
      }
    }
    // else {
    //   values.Calculated = 0;
    // }
  };

  const updateCalculatedValue = (setFieldValue, values) => {
    if (values.DipBefore?.length === 0 || values.DipAfter?.length === 0) {
      setFieldValue('Calculated', 0);
      ambientValidationCheck({ Calculated: 0, ...values });
    } else {
      const newCalculated = values.DipAfter - values.DipBefore;
      setFieldValue('Calculated', newCalculated);
      ambientValidationCheck({ ...values, Calculated: newCalculated });
    }
  };

  const updateAggregateAutoCalculation = (setFieldValue, dipBefore) => {
    if (dipBefore === '' || dipBefore === null || dipBefore === undefined) {
      setFieldValue('DipAfter', '');
      setFieldValue('Calculated', 0);
      setFieldValue('ActualDischargeQty', '');
      return;
    }

    const calculated = Number(aggregateQtyAmbient) || 0;
    setFieldValue('DipAfter', Number(dipBefore) + calculated);
    setFieldValue('Calculated', calculated);
    setFieldValue('ActualDischargeQty', calculated);
  };

  const onSubmit = async (values) => {
    const inputValues = {
      DropId: getDropId(),
      Product: values.Product,
      Tank: values.Tank,
      Capacity: virtualTerminal ? 0 : parseInt(values.Capacity, 10),
      Compartments: values.Compartments,
      ActualDischargeQty: parseInt(values.ActualDischargeQty, 10),
      DipAfter: virtualTerminal ? 0 : parseInt(values.DipAfter, 10),
      DipBefore: virtualTerminal ? 0 : parseInt(values.DipBefore, 10),
      Calculated: virtualTerminal ? 0 : parseInt(values.Calculated, 10),
      TASVehicleID: getSelectedVehicle,
      QuantityAmbient: values.QuantityAmbient,
      QuantityStandard: values.QuantityStandard,
      Aggregate: false,
      MultiLoadId: null,
    };
    const idArray = values.LoadIdAddon.split(',').map((loadId) => loadId.trim());
    const skArray = [];
    idArray.push(values.LoadIdAddon);
    idArray.forEach((myID) => {
      skArray.push(generateDropDetailsSK(selectedDocket, myID));
    });
    
    if (aggregateFlag && allLidSelected) {
      /* eslint-disable-next-line */
      for (const mySK of skArray) {
        if (mySK.split(',').length <= 1) {
          /* eslint-disable no-await-in-loop */
          // await createDropDetails(graphClient, {
          //   PK: selectedDocket.PK,
          //   SK: mySK,
          // }, {
          //   ...inputValues,
          //   ActualDischargeQty: productAmbient[mySK.split('DROPDETAILS#')[1]],
          // });
        } else {
          /* eslint-disable no-await-in-loop */
          // await createDropDetails(graphClient, {
          //   PK: selectedDocket.PK,
          //   SK: mySK,
          // }, {
          //   ...inputValues,
          //   Aggregate: true,
          // });
        }        
      } 
      //Calculate and add discharge quantity for products in case of multiple load ids
      await getProductIdData(idArray, inputValues, selectedDocket);
    } else {
      await createDropDetails(graphClient, {
        PK: selectedDocket.PK,
        SK: generateDropDetailsSK(selectedDocket, selectedLoadIdAddon),
      }, inputValues);      
    }
    history.push('/drop-details');
  };

  const getInitialValues = () => {
    const overrideValues = {};

    // Open order has additional required fields in the validation schema
    overrideValues.OpenOrder = isOpenOrder;
    overrideValues.VirtualTerminal = virtualTerminal;

    const loadIdAddons = getLoadIdAddons();

    if (loadIdAddons.length === 1) {
      overrideValues.LoadIdAddon = loadIdAddons[0];
    }
    const products = getProducts({ LoadIdAddon: loadIdAddons[0] });
    if (products && products.length === 1) {
      overrideValues.Product = products[0].Name;
    }

    // !!! Need to be improved: only need to reset thoes values when there are certain values
    // pre-populated into
    overrideValues.DipBefore = virtualTerminal ? 0 : '';
    overrideValues.DipAfter = virtualTerminal ? 0 : '';
    overrideValues.Calculated = virtualTerminal ? 0 : '';
    overrideValues.ActualDischargeQty = '';

    // Pre-select values for load id addon and product if only one value is present
    return { ...initialValues, ...overrideValues };
  };

  const handleFormReset = (resetForm, values) => {
    const loadIdReset = getLoadIdAddons().length <= 1 ? values.LoadIdAddon : '';
    const dipAfterReset = virtualTerminal ? 0 : '';
    const dipBeforeReset = virtualTerminal ? 0 : '';
    const calculatedReset = virtualTerminal ? 0 : '';
    const actualDischargeQty = '';

    resetForm({
      values: {
        ...initialValues,
        LoadIdAddon: loadIdReset,
        DipAfter: dipAfterReset,
        DipBefore: dipBeforeReset,
        Calculated: calculatedReset,
        ActualDischargeQty: actualDischargeQty,
        VirtualTerminal: virtualTerminal,
      },
      errors: {}, // clear validation errors explicitly
      touched: {}, // clear touched fields
    });
    setSelectedProduct('');
    setQuantityWarning();
    //For hiding vehicle id and disable auto calculation checkbox
    if (Array.isArray(selectedLoadIdAddon) && selectedLoadIdAddon.length > 1) {
      setAllLidSelected(false);
      setFormReset(true);
    } else if (Array.isArray(selectedLoadIdAddon) && selectedLoadIdAddon.length === 1) {
      setAllLidSelected(true);
      setFormReset(false);
    } else {
      setFormReset(true);
    }
  };

  // handle form reset single select for aggregate drop
  const handleFormResetAggregateSingle = (resetForm) => {
    const dipAfterReset = virtualTerminal ? 0 : '';
    const dipBeforeReset = virtualTerminal ? 0 : '';
    const calculatedReset = 0;
    const actualDischargeQty = '';
    const loadIdReset = selectedLoadIdAddon;
    resetForm({
      values: {
        ...initialValues,
        LoadIdAddon: loadIdReset,
        DipAfter: dipAfterReset,
        DipBefore: dipBeforeReset,
        Calculated: calculatedReset,
        ActualDischargeQty: actualDischargeQty,
        VirtualTerminal: virtualTerminal,
      },
      errors: {}, // clear validation errors explicitly
      touched: {}, // clear touched fields     
    });
    setQuantityWarning();
    setFormReset(false); 
  };

  // handle form reset single select for aggregate drop
  const handleFormResetAggregateMulti = (resetForm, values, isManualEntry) => {
    const isManualMode = !!isManualEntry || !!virtualTerminal || !!isDPTerminal;
    const dipAfterReset = isManualMode ? 0 : '';
    const dipBeforeReset = virtualTerminal ? 0 : '';
    const calculatedReset = isManualMode ? 0 : aggregateQtyAmbient;
    // const calculatedReset = aggregateQtyAmbient;
    const actualDischargeQty = isManualMode ? '' : aggregateQtyAmbient;
    const loadIdReset = values.LoadIdAddon;

    // const loadIdReset = values.loadIdAddons ? values.loadIdAddons.length > 1
    //   ? values.loadIdAddons[0] : values.loadIdAddons : selectedLoadIdAddon[0];

    resetForm({
      values: {
        ...initialValues,
        LoadIdAddon: loadIdReset,
        DipAfter: dipAfterReset,
        DipBefore: dipBeforeReset,
        Calculated: calculatedReset,
        ActualDischargeQty: actualDischargeQty,
        VirtualTerminal: virtualTerminal,
      },
      errors: {}, // clear validation errors explicitly
      touched: {}, // clear touched fields
    });
    setQuantityWarning();
    setFormReset(false); //Form reset
  };

  const fetchAndSetAllDropDetails = async () => {
    try {
      const shDetails = await getShipmentDetails(graphClient, shipmentId, carrierId);
      if (shDetails?.data?.getShipmentDetails) {
        setShipmentDetailsForDelivery(shDetails.data.getShipmentDetails);
        shDetails.data.getShipmentDetails.forEach(
          async (sd) => {
            // As sd.SK is always in format like: DeliveryNo#ORIGINAL#204077233#LID#820641 or
            // DeliveryNo#REDIRECT#204077232-R1#LID#820641, so:
            // 1. sd.SK.split('#')[2] is used to obatin the Delivery Docket number
            // 2. split('-')[0] is used to extract the Delivery Docket number without '-R'
            if (sd.Status !== statuses.Void && sd.SK.split('#')[2].split('-')[0] === deliveryId.split('-')[0]) {
              const response = await getDeliveryDropDetails(graphClient, sd);
              if (response?.data?.getDropDetails) {
                // We have to set the state here because drop detail fetch is async
                setDropDetailsAll(
                  // (oldArray) => {
                  //   const merged = flatten([...oldArray, response.data.getDropDetails]);
                  //   const bySk = new Map();
                  //   merged.forEach((item) => {
                  //     if (item?.SK && !bySk.has(item.SK)) {
                  //       bySk.set(item.SK, item);
                  //     }
                  //   });
                  //   return Array.from(bySk.values());
                  // },
                  (oldArray) => flatten([...oldArray, response.data.getDropDetails]),
                );
              }
            }
          },
        );
      }
    } catch (error) {
      handleGraphApiError(error);
    }
  };

  // This func() is to judge whether Aggregate Drop Details should be enabled
  const aggregateFlagCondition = () => {
    // The aggregate drop details should be disabled when there
    // is only one lid
    const loadIdAddons = getLoadIdAddonsWithAggregate();
    if (loadIdAddons?.length === 1) {
      setAggregateFlag(false);
    }
    // If 1 or more drop details already existed
    // Then aggregate drop details should be disabled
    if (dropDetailsAll) {
      normalizedDropDetails.forEach((record) => {
        if (record.DROPS?.length > 0) {
          // setAggregateFlag(false);
          setDropDetailsExists(true);
        }
      });
    }
    // If there are more than 1 product per lid or different products across
    // each lid, aggregate drop details should be disabled
    shipmentDetails?.forEach((test) => {
      if (test.Products && test.Products.length > 1) {
        setAggregateFlag(false);
      } else if (test.Products && aggregateProduct === '') {
        setAggregateProduct(test.Products[0].Name);
      } else if (test.Products && test.Products[0].Name !== aggregateProduct) {
        setAggregateFlag(false);
      }
    });
  };

  // This func() is to sum qty amb. across lids
  const aggregateSumAmbient = () => {
    const loadIdAddons = getLoadIdAddonsWithAggregate();
    let temp = 0;
    const productList = {};
    if (loadIdAddons) {
      loadIdAddons?.forEach((lidtest) => {
        const selectedDetail = shipmentDetails.find((sd) => sd.SK.includes(`LID#${lidtest}`));
        if (selectedDetail) {
          productList[selectedDetail.SK] = parseInt(selectedDetail.Products[0].QuantityAmbient, 10);
          temp += parseInt(selectedDetail.Products[0].QuantityAmbient, 10);
        }
      });
      //Calculate aggregate quantity ambient in case of multiple load ids
      let totalDischargeQty = getTotalDeliverd(normalizedDropDetails);
      if(totalDischargeQty>0){
        setAggregateQtyAmbient(temp - totalDischargeQty);
      } else {
        setAggregateQtyAmbient(temp);
      }
      SetProductAmbient(productList);
    }
  };

  // This func() is used to pre-populate data into enterDropDetails page
  const setAggregateInitals = () => {
    const aggregateInitials = { OpenOrder: isOpenOrder, VirtualTerminal: virtualTerminal };
    const loadIdAddons = getLoadIdAddonsWithAggregate();
    if (loadIdAddons.length === 1) {
      aggregateInitials.LoadIdAddon = loadIdAddons[0];
      const products = getProducts({ LoadIdAddon: loadIdAddons[0] });
      if (products && products.length === 1) {
        aggregateInitials.Product = products[0].Name;
      }
    } else if (loadIdAddons.length > 1) {
      aggregateInitials.LoadIdAddon = JSON.stringify(loadIdAddons[loadIdAddons.length - 1]);
      aggregateInitials.LoadIdAddon = aggregateInitials.LoadIdAddon.replace('[', '');
      aggregateInitials.LoadIdAddon = aggregateInitials.LoadIdAddon.replace(']', '');
      aggregateInitials.LoadIdAddon = aggregateInitials.LoadIdAddon.replaceAll('"', '');
      const products = getProducts({ LoadIdAddon: loadIdAddons[0] });
      if (products && products.length === 1) {
        aggregateInitials.Product = products[0].Name;
      }
    }
    aggregateInitials.DipBefore = virtualTerminal ? 0 : (isDPTerminal ? '' : 0);
    aggregateInitials.Calculated = virtualTerminal ? 0 : (isDPTerminal ? '' : aggregateQtyAmbient);
    aggregateInitials.ActualDischargeQty = (virtualTerminal || isDPTerminal) ? '' : aggregateQtyAmbient;
    aggregateInitials.DipAfter = virtualTerminal ? 0 : (isDPTerminal ? '' : 0);
    return Object.assign(initialValues, aggregateInitials);
  };

  useEffect(() => {
    const formik = formikRef.current;
    if (!formik || formik.dirty || getLoadIdAddons().length === 0) return;

    const defaults = aggregateFlag ? setAggregateInitals() : getInitialValues();
    formik.setValues((currentValues) => ({
      ...currentValues,
      LoadIdAddon: defaults.LoadIdAddon || '',
      Product: defaults.Product || '',
    }), true);
  }, [shipmentDetails, aggregateFlag]);

  useEffect(() => {
    const getDataForComponent = async () => {
      const dDetails = await getDeliveryDropDetails(graphClient, selectedDocket);
      if (dDetails?.data?.getDropDetails) {
        setDropDetails(dDetails.data.getDropDetails);
      }
    };
    getDataForComponent()
      .catch((error) => handleGraphApiError(error));
    fetchAndSetAllDropDetails();
  }, [deliveryId]);

  // This useEffect is for judge aggregate drop details
  useEffect(() => {
    if (!isOpenOrder) {
      aggregateFlagCondition();
      aggregateSumAmbient();
    } else {
      setAggregateFlag(false); // The aggregate drop details should not be applied to open-orders
    }
  }, [shipmentDetails, dropDetailsAll]);

  // If running in a virtual terminal, enable "Don't auto-calculate" behaviour by default
  useEffect(() => {
    if (virtualTerminal || isDPTerminal) {
      console.log('Virtual terminal detected - enabling manual entry by default');
      setIsNumberInputDisabled(virtualTerminal || isDPTerminal);
    }
  }, [virtualTerminal, isDPTerminal]);

  return (
    <Container className="enter-drop-details-container pl-0 pr-0" data-testid="enter-drop-details-container">
      <NetworkSpeedChecker />
      {/* If there is no drop details linked to a docket, */}
      {/* the back button points jumps to Page /delivery-docket. */}
      {/* Use flatMap() so that we can go through each dropDeatil of a docket */}
      {/* Disable the back button when page is still loading */}
      {
        dropDetails
          ? (
            dropDetails.length > 0 && dropDetails.flatMap((d) => d.DROPS).length > 0
              ? (
                <Link className="link" to="/drop-details" onClick={() => backButtonClickHandler(pageTitleConstant.dropDetailsForm)}>
                  <BackButton />
                </Link>
              )
              : (
                <Link className="link" to="/delivery-docket" onClick={() => backButtonClickHandler(pageTitleConstant.dropDetailsForm)}>
                  <BackButton />
                </Link>
              )
          ) : (
            <Link className="link" to="/delivery-docket" onClick={(event) => event.preventDefault()}>
              <BackButton />
            </Link>
          )
      }

      <PageHeading label="Delivery docket number" refNumber={getShipmentHeader(deliveryId)} />

      <Formik
        innerRef={formikRef}
        initialValues={aggregateFlag ? setAggregateInitals() : getInitialValues()}
        validationSchema={DropDetailsSchema}
        validateOnMount
        onSubmit={(values, formikBag) => onSubmit(values, formikBag)}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          isSubmitting,
          isValid,
          values,
          errors,
          touched,
          dirty,
          resetForm,
          setFieldValue,
          setFieldTouched,
          validateField,
        }) => (
          <Form noValidate onSubmit={handleSubmit} data-testid="enter-drop-details-form">
            <Row>
              <Col>

                {!isOpenOrder && (
                  <Row>
                    <LidDetail
                      shipmentDetails={shipmentDetails}
                      dropDetailsAll={normalizedDropDetails}
                      sendDataBackToParent={sendDataBackToParent}
                    />
                  </Row>
                )}

                <Row className="mb-8">
                  <Col xs="12">
                    <div className="drop-details-form-title"><h5>Enter drop details</h5></div>
                  </Col>

                  <Col xs="12">
                    <div className="drop-details-form-subtitle">Enter details for each drop, one at a time.</div>
                  </Col>
                </Row>

                {/* 'input-colour' - this css class is used globally for all input fields which is
                  defined in _enterDropDetails.scss */}
                <Row>
                  {!isOpenOrder && (
                    /* Field: LoadIdAddon */
                    <Col xs="12" md="6">
                      {!aggregateFlag ? (
                        <FormGroup className="mb-10 mb-md-8">
                          <InputGroup>
                            <Label for="LoadIdAddon" className="d-flex align-items-center">
                              Load ID Addon:
                            </Label>
                          </InputGroup>
                          <InputGroup>
                            <CustomInput
                              type="select"
                              name="LoadIdAddon"
                              id="LoadIdAddon-select"
                              data-testid="LoadIdAddon-select"
                              value={values.LoadIdAddon}
                              onChange={(e) => {
                                handleChange(e);
                                setSelectedLoadIdAddon(e.target.value);
                                setFieldValue('Product', '');
                                handleFormResetAggregateSingle(resetForm, values);                                
                                if (virtualTerminal || isDPTerminal) {
                                  setIsNumberInputDisabled(virtualTerminal);
                                }
                                // handleFormResetAggregateMulti(resetForm, values);
                              }}
                              onBlur={handleBlur}
                              invalid={Boolean(errors.LoadIdAddon) && touched.LoadIdAddon}
                              bsSize="lg"
                              disabled={getLoadIdAddons().length <= 1}
                              onClick={
                                () => lidAddonClickHandler(pageTitleConstant.dropDetailsForm)
                              }
                              className="input-colour"
                            >
                              <option key="" value="" hidden>Select an option...</option>
                              {
                                getLoadIdAddons().map(
                                  (addon) => (
                                    <option
                                      key={addon}
                                      value={addon}
                                      data-testid={`LoadIdAddon-option-${addon}`}
                                    >
                                      {addon}
                                    </option>
                                  ),
                                )
                              }

                            </CustomInput>
                            <FormFeedback type="invalid" data-testid="LoadIdAddon-feedback">{errors.LoadIdAddon}</FormFeedback>
                          </InputGroup>
                        </FormGroup>
                      ) : (
                        // If it is a aggregate drop detail
                        <FormGroup className="mb-10 mb-md-8">

                          <InputGroup>
                            <Label for="LoadIdAddon" className="d-flex align-items-center">
                              Load ID Addon:
                            </Label>
                          </InputGroup>
                          <InputGroup>
                            <CustomInput
                              type="select"
                              name="LoadIdAddon"
                              id="LoadIdAddon-select"
                              data-testid="LoadIdAddon-select"
                              value={values.LoadIdAddon}
                              onChange={(e) => {
                                if (e.target.value.split(',').length > 1) {
                                  handleChange(e);
                                  setSelectedLoadIdAddon(e.target.value.split(','));
                                  setAllLidSelected(false);
                                  handleFormResetAggregateMulti(resetForm, values);
                                } else {
                                  handleChange(e);
                                  setSelectedLoadIdAddon(e.target.value);
                                  setAllLidSelected(true);
                                  if (virtualTerminal || isDPTerminal) {
                                    setIsNumberInputDisabled(virtualTerminal || isDPTerminal);
                                  }
                                  handleFormResetAggregateSingle(resetForm, values);
                                }
                                setFieldValue('Product', aggregateProduct);
                                setFieldValue('LoadIdAddon', e.target.value);
                              }}
                              onBlur={handleBlur}
                              invalid={Boolean(errors.LoadIdAddon) && touched.LoadIdAddon}
                              bsSize="lg"
                              disabled={getLoadIdAddons().length <= 1}
                              onClick={
                                () => lidAddonClickHandler(pageTitleConstant.dropDetailsForm)
                              }
                              className="input-colour"
                            >
                              <option key="" value="" hidden>Select an option...</option>

                              {
                                getLoadIdAddonsWithAggregate().map(
                                  (addon) => (
                                    <option
                                      key={addon}
                                      value={addon}
                                      data-testid={`LoadIdAddon-option-${addon}`}
                                    >
                                      {(typeof (addon) === 'string') ? addon : `ALL(${addon})`}
                                    </option>
                                  ),
                                )
                              }
                            </CustomInput>
                          </InputGroup>

                          {
                            !!errors.LoadIdAddon && touched.LoadIdAddon
                              ? <FormText color="error" data-testid="LoadIdAddon-feedback">{errors.LoadIdAddon}</FormText>
                              : <FormText color="help" data-testid="LoadIdAddon-info">Select option ‘ALL’ to drop all the fuel in the same tank. This option is only available when no other drop detail has been added.</FormText>
                          }
                        </FormGroup>
                      )}

                    </Col>
                  )}
                  <Col xs="12" md="6">
                    {((!aggregateFlag && !formReset && !isOpenOrder) || (aggregateFlag && !allLidSelected && !formReset && !isOpenOrder && !virtualTerminal && !isDPTerminal)) && (
                    <span>
                      <Label for="TASVehicleID" className="vehicle-row mt-md-8">Vehicle:&nbsp;</Label>
                      <Label
                        name="TASVehicleID"
                        data-testid="TASVehicleID"
                        className="vehicle-no"
                        value={getSelectedVehicle}
                      >
                        <b>{getSelectedVehicle}</b>
                      </Label>
                    </span>

                    )}
                    {allLidSelected && !formReset && !isOpenOrder && !virtualTerminal && !isDPTerminal && (
                      <div className="d-flex align-items-center mt-4">
                        <Label><input type="checkbox" checked={isNumberInputDisabled} className="ml-2" disabled={aggregateFlag && !allLidSelected} id="ww" onChange={(e) => { setIsNumberInputDisabled((prev) => !prev); aggregateFlagCondition(); aggregateSumAmbient(); handleFormResetAggregateMulti(resetForm, values, e.target.checked); setTimeout(() => ambientValidationCheck({ ...values, ActualDischargeQty: 0 }), 0); }} /></Label>
                        <Label className="ml-2 drop-details-form-subtitle drop-check-title">Don’t auto-calculate drop info</Label>
                      </div>
                    )}
                  </Col>
                  <Col />
                  <Col xs="12">
                    <div className="tablet-divider" />
                  </Col>

                </Row>

                <Row>
                  {/* Field: Tank */}
                  <Col xs="12" md="6">
                    <FormGroup className="mb-10">
                      <Label for="Tank">
                        Tank Number / Name -
                        {' '}
                        <span className="optional">optional</span>
                        :
                      </Label>
                      <InputGroup className="input-colour">
                        <Input
                          type="text"
                          name="Tank"
                          data-testid="Tank-field"
                          value={values.Tank}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={!!errors.Tank && touched.Tank}
                          autoComplete="off"

                        />

                        <FormFeedback type="invalid" data-testid="Tank-feedback">{errors.Tank}</FormFeedback>
                      </InputGroup>
                    </FormGroup>
                  </Col>

                  {/* Field: Product */}
                  <Col xs="12" md="6">
                    <FormGroup className="mb-10">
                      <Label for="product">Product:</Label>
                      {
                        !aggregateFlag ? (
                          <InputGroup>
                            <CustomInput
                              type="select"
                              name="Product"
                              id="Product-select"
                              data-testid="Product-select"
                              value={values.Product}
                              onChange={(e) => {
                                handleChange(e);
                                setSelectedProduct(e.target.value);
                                setFieldTouched('Capacity', false, false);
                                setFieldTouched('DipBefore', false, false);
                                setFieldTouched('DipAfter', false, false);
                                setFieldTouched('QuantityAmbient', false, false);
                                setFieldTouched('QuantityStandard', false, false);
                                setFieldTouched('ActualDischargeQty', false, false);
                              }}
                              onBlur={handleBlur}
                              invalid={!!errors.Product && touched.Product}
                              bsSize="lg"
                              disabled={getProducts(values).length < 1}
                              onClick={() => productClickHandler(pageTitleConstant.dropDetailsForm)}
                              className="input-colour"
                            >
                              <option key="" value="" hidden>Select an option...</option>
                              {
                                getProducts(values).map(
                                  (product) => (
                                    <option
                                      key={product.Name}
                                      value={product.Name}
                                      data-testid={`Product-option-${product.Name}`}
                                    >
                                      {product.Name}
                                    </option>
                                  ),
                                )
                              }
                            </CustomInput>
                            <FormFeedback type="invalid" data-testid="Product-feedback">{errors.Product}</FormFeedback>
                          </InputGroup>
                        ) : (
                          <InputGroup>
                            <Input
                              type="string"
                              name="Product"
                              id="Product-select"
                              data-testid="Product-select"
                              value={values.Product}
                              autoComplete="off"
                              readOnly
                            />
                          </InputGroup>
                        )
                      }

                      {
                        !isOpenOrder && !isDPTerminal && !virtualTerminal && (
                          <FormText color="help" className="d-inline" data-testid="qty-ambient">Qty ambient remaining: </FormText>
                        )
                      }

                      {
                        !isOpenOrder && !isDPTerminal && !virtualTerminal && (
                          (!aggregateFlag || (aggregateFlag && !allLidSelected)) ? (
                            getSelectedProducts()?.filter((product) => product.Name === values.Product)
                              .map((product) => (
                                <FormText key={product} color="help" className="qty-ambient d-inline">
                                  {
                                      deliveredTotals
                                      .get(selectedLoadIdAddon + product.Name)
                                      ? (product.QuantityAmbient
                                          - deliveredTotals
                                          .get(selectedLoadIdAddon + product.Name)).toFixed(2)
                                      : product.QuantityAmbient
                                  }
                                </FormText>
                              ))
                          ) : (
                            <FormText color="help" className="qty-ambient d-inline">
                              {aggregateQtyAmbient.toFixed(2)}
                            </FormText>
                          )

                        )
                      }

                    </FormGroup>
                  </Col>

                  {
                    isOpenOrder && (
                      /* Field: QuantityAmbient */
                      <Col xs="12" md="6">
                        <FormGroup className="mb-10">
                          <Label for="QuantityAmbient">
                            Quantity Ambient:
                          </Label>
                          <InputGroup className="input-colour">
                            <Input
                              type="number"
                              name="QuantityAmbient"
                              data-testid="QuantityAmbient-field"
                              value={values.QuantityAmbient}
                              onChange={(e) => {
                                handleChange(e);                                
                                const warningIsActive = updateOpenOrderAmbientWarning({
                                  ...values,
                                  QuantityAmbient: e.target.value,
                                });
                                setFieldValue('Product', values.Product);
                                setFieldTouched('QuantityAmbient', false, false);
                                setFieldTouched('Capacity', false, false);
                                setFieldTouched('QuantityStandard', false, false);
                                setFieldTouched('DipBefore', false, false);
                                setFieldTouched('DipAfter', false, false);
                                setFieldTouched('ActualDischargeQty', false, false);
                              }}
                              onBlur={handleBlur}
                              invalid={!!errors.QuantityAmbient && touched.QuantityAmbient}
                              autoComplete="off"
                            />
                          </InputGroup>
                          {
                            !!errors.QuantityAmbient && touched.QuantityAmbient
                              ? <FormText color="error" data-testid="QuantityAmbient-feedback">{errors.QuantityAmbient}</FormText>
                              : (
                                <FormText color="help" data-testid="QuantityAmbient-info">
                                  Enter the qty. ambient value of the product
                                  as mentioned in the Bill of Lading.
                                </FormText>
                              )
                          }
                        </FormGroup>
                      </Col>
                    )
                  }

                  {
                    isOpenOrder && (
                      /* Field: QuantityStandard */
                      <Col xs="12" md="6">
                        <FormGroup className="mb-10">
                          <Label for="QuantityStandard">
                            Quantity Standard (15˚C):
                          </Label>
                          <InputGroup className="input-colour">
                            <Input
                              type="number"
                              name="QuantityStandard"
                              data-testid="QuantityStandard-field"
                              value={values.QuantityStandard}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={!!errors.QuantityStandard && touched.QuantityStandard}
                              autoComplete="off"
                            />
                          </InputGroup>
                          {
                            !!errors.QuantityStandard && touched.QuantityStandard
                              ? <FormText color="error" data-testid="QuantityStandard-feedback">{errors.QuantityStandard}</FormText>
                              : (
                                <FormText color="help" data-testid="QuantityStandard-info">
                                  Enter the qty. standard value of the product
                                  as mentioned in the Bill of Lading.
                                </FormText>
                              )
                          }
                        </FormGroup>
                      </Col>
                    )
                  }

                  {/* Field: Capacity */}
                  {!virtualTerminal && (
                  <Col xs="12" md="6">
                    <FormGroup className="mb-10">
                      <Label for="Capacity">
                        Capacity (SFL)
                      </Label>
                      <InputGroup className="input-colour">
                        <Input
                          type="number"
                          name="Capacity"
                          data-testid="Capacity-field"
                          value={values.Capacity}
                          onChange={handleChange}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          invalid={!!errors.Capacity && touched.Capacity}
                          onBlur={(e) => {
                            if (e.target.value) {
                              e.target.value = parseInt(e.target.value.split('.')[0], 10);
                            }
                            handleChange(e); // eslint-disable-next-line
                            touched.Capacity = true;
                          }}
                          autoComplete="off"
                        />
                      </InputGroup>
                      {
                        !!errors.Capacity && touched.Capacity
                          ? <FormText color="error" data-testid="Capacity-feedback">{errors.Capacity}</FormText>
                          : <FormText color="help" data-testid="Capacity-info">Only whole numbers are accepted for this field.</FormText>
                      }
                    </FormGroup>
                  </Col>
                  )}

                  {/* Field: Compartments */}
                  <Col xs="12" md="6">
                    <FormGroup className="mb-10">
                      <Label for="Compartments">
                        Compartment(s) -
                        <span className="optional">optional</span>
                        :
                      </Label>
                      <InputGroup className="input-colour">
                        <Input
                          type="tel"
                          name="Compartments"
                          data-testid="Compartments-field"
                          value={values.Compartments}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={!!errors.Compartments}
                          autoComplete="off"
                        />
                      </InputGroup>
                      {
                        errors.Compartments
                          ? <FormText color="error" data-testid="Compartments-feedback">{errors.Compartments}</FormText>
                          : <FormText color="help" data-testid="Compartments-info">Only numeric values, commas (,), and hyphens (-) are permitted. Each compartment may contain up to 20 digits. Use commas to separate multiple compartments.</FormText>
                      }
                    </FormGroup>
                  </Col>

                  {/* Field: DipBefore */}
                  <Col xs="12" md="6" hidden={virtualTerminal}>
                    <FormGroup className="mb-10">
                      <Label for="DipBefore">Dip Before (B):</Label>
                      <InputGroup className="input-colour">
                        <Input
                          type="number"
                          name="DipBefore"
                          data-testid="DipBefore-field"
                          value={values.DipBefore}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            if (!!aggregateFlag && !!allLidSelected) {
                              if (isNumberInputDisabled) {
                                setFieldValue('DipAfter', '');
                              } else {
                                updateAggregateAutoCalculation(setFieldValue, e.target.value);
                              }
                            } else {
                              updateCalculatedValue(
                                setFieldValue,
                                { ...values, DipBefore: e.target.value },
                              );
                            }
                            handleChange(e);
                          }}
                          onBlur={(e) => {
                            if (e.target.value) {
                              e.target.value = parseInt(e.target.value.split('.')[0], 10);
                            }
                            if(!virtualTerminal && !isDPTerminal) {
                              if (!!aggregateFlag && !!allLidSelected) {
                                if (isNumberInputDisabled) {
                                  setFieldValue('DipAfter', '');
                                } else {
                                  updateAggregateAutoCalculation(setFieldValue, e.target.value);
                                }
                              } else {
                                updateCalculatedValue(
                                  setFieldValue,
                                  { ...values, DipBefore: e.target.value },
                                );
                              }
                            }
                            handleChange(e); // eslint-disable-next-line
                            touched.DipBefore = true;
                          }}
                          invalid={!!errors.DipBefore && touched.DipBefore}
                          autoComplete="off"
                        />
                      </InputGroup>
                      {
                        !!errors.DipBefore && touched.DipBefore
                          ? <FormText color="error" data-testid="DipBefore-feedback">{errors.DipBefore}</FormText>
                          : <FormText color="help" data-testid="DipBefore-info">Only whole numbers are accepted for this field.</FormText>
                      }
                    </FormGroup>
                  </Col>

                  {/* Field: DipAfter */}
                  <Col xs="12" md="6" hidden={virtualTerminal}>
                    <FormGroup className="mb-10">
                      <Label for="DipAfter">Dip After (A):</Label>
                      <InputGroup className="input-colour">
                        <Input
                          type="number"
                          name="DipAfter"
                          data-testid="DipAfter-field"
                          value={values.DipAfter}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            updateCalculatedValue(
                              setFieldValue,
                              { ...values, DipAfter: e.target.value },
                            );
                            handleChange(e);
                          }}
                          onBlur={(e) => {
                            if (e.target.value) {
                              e.target.value = parseInt(e.target.value.split('.')[0], 10);
                            }
                            updateCalculatedValue(
                              setFieldValue,
                              { ...values, DipAfter: e.target.value },
                            );
                            handleChange(e); // eslint-disable-next-line
                            touched.DipAfter = true;
                          }}
                          invalid={!!errors.DipAfter && touched.DipAfter}
                          autoComplete="off"
                          disabled={!!aggregateFlag && !!allLidSelected && !isNumberInputDisabled}
                        />
                      </InputGroup>

                      {(aggregateFlag && allLidSelected) ? (
                        (isNumberInputDisabled)
                          ? ((!!errors.DipAfter && touched.DipAfter)
                            ? <FormText color="error" data-testid="DipAfter-feedback">{errors.DipAfter}</FormText>
                            : <FormText color="help" data-testid="DipAfter-info">Only whole numbers are accepted for this field.</FormText>
                          )
                          : <FormText className="text-help">We will calculate this for you.</FormText>
                      ) : (
                        (!!errors.DipAfter && touched.DipAfter)
                          ? <FormText color="error" data-testid="DipAfter-feedback">{errors.DipAfter}</FormText>
                          : <FormText color="help" data-testid="DipAfter-info">Only whole numbers are accepted for this field.</FormText>
                      )}

                      {/* {(!aggregateFlag && !allLidSelected && !isNumberInputDisabled) ? (
                        <FormText color="warn" className="text-help"> ddddd </FormText>
                      ) : 'hjkjh'} */}
                    </FormGroup>
                  </Col>

                  {/* Field: Calculated */}
                  <Col xs="12" md="6" hidden={virtualTerminal}>
                    <FormGroup className="mb-10">
                      <Label for="Calculated">Calculated (A - B):</Label>
                      <InputGroup className="input-colour">
                        <Input
                          type="number"
                          name="Calculated"
                          data-testid="Calculated-field"
                          value={values.Calculated}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={!!errors.Calculated && touched.Calculated}
                          autoComplete="off"
                          disabled
                        />
                      </InputGroup>
                      {
                        (!!errors.Calculated && touched.Calculated)
                          ? <FormText color="error" data-testid="Calculated-feedback">{errors.Calculated}</FormText>
                          : <FormText color="help" data-testid="Calculated-info">We will calculate this for you.</FormText>
                      }
                    </FormGroup>
                  </Col>

                  {/* Field: ActualDischargeQty */}
                  <Col xs="12" md="6">
                    <FormGroup className="mb-10">
                      <Label for="ActualDischargeQty">Actual Discharge Quantity:</Label>
                      <InputGroup className="input-colour">
                        <Input
                          type="number"
                          name="ActualDischargeQty"
                          data-testid="ActualDischargeQty-field"
                          value={values.ActualDischargeQty}
                          onChange={(e) => {
                            handleChange(e);
                            const warningIsActive = ambientValidationCheck(
                              { ...values, ActualDischargeQty: e.target.value },
                            );
                            setFieldTouched('Product', false, false);
                            setFieldTouched('Capacity', false, false);
                            setFieldTouched('DipBefore', false, false);
                            setFieldTouched('DipAfter', false, false);
                            setFieldTouched('QuantityAmbient', false, false);
                            setFieldTouched('QuantityStandard', false, false);
                            setFieldTouched('ActualDischargeQty', false, false);
                          }}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value && e.target.value !== 0) {
                              e.target.value = parseInt(e.target.value.split('.')[0], 10);
                            }
                            updateCalculatedValue(
                              setFieldValue,
                              { ...values, ActualDischargeQty: e.target.value },
                            );
                            handleChange(e); // eslint-disable-next-line
                            touched.ActualDischargeQty = true;
                          }}
                          invalid={
                            ((!!errors.ActualDischargeQty && touched.ActualDischargeQty)
                              && !(virtualTerminal && isAmbientQuantityWarning(errors.ActualDischargeQty)))
                            || (!!quantityWarning
                              && !(virtualTerminal && isAmbientQuantityWarning(quantityWarning)))
                          }
                          autoComplete="off"
                          disabled={!!aggregateFlag && !!allLidSelected && !isNumberInputDisabled}
                        />
                      </InputGroup>
                      {(aggregateFlag && allLidSelected) ? (
                        (isNumberInputDisabled)
                            ? ((!!errors.ActualDischargeQty && touched.ActualDischargeQty
                              && !(virtualTerminal && isAmbientQuantityWarning(errors.ActualDischargeQty)))
                            ? <FormText color="error" data-testid="ActualDischargeQty-feedback">{errors.ActualDischargeQty}</FormText>
                              : quantityWarning && !(virtualTerminal && isAmbientQuantityWarning(quantityWarning))
                                ? <FormText color="warn" data-testid="ActualDischargeQty-feedback">{quantityWarning}</FormText> : ''
                          )
                          : <FormText className="text-help">We will calculate this for you.</FormText>
                      ) : (
                        (isOpenOrder && quantityWarning
                          ? <FormText color="warn" data-testid="ActualDischargeQty-feedback">{quantityWarning}</FormText>
                          : (!!errors.ActualDischargeQty && touched.ActualDischargeQty
                          && !(virtualTerminal && isAmbientQuantityWarning(errors.ActualDischargeQty)))
                          ? <FormText color="error" data-testid="ActualDischargeQty-feedback">{errors.ActualDischargeQty}</FormText>
                          : (quantityWarning && !(virtualTerminal && isAmbientQuantityWarning(quantityWarning))
                            && <FormText color="warn" data-testid="ActualDischargeQty-feedback">{quantityWarning}</FormText>
                          ))
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12">

                    <PrimaryButton
                      disabled={isSubmitting || !isValid || !dirty || ambientDisable}
                      className="submit-button"
                      data-testid="save-button"
                      type="Submit"
                      label="Save"
                      tabIndex={0}
                      onClick={() => saveClickHandler(pageTitleConstant.dropDetailsForm)}
                    />
                  </Col>
                  <Col xs="12" className="mt-6">
                    <div
                      className="green-link"
                      data-testid="clear-all-link"
                      onClick={() => {
                        if (virtualTerminal || isDPTerminal) {
                          setIsNumberInputDisabled(virtualTerminal || isDPTerminal);
                        }
                        handleFormReset(resetForm, values);
                        clearFieldsClickHandler(pageTitleConstant.dropDetailsForm);
                      }}
                      role="button"
                    >
                      Clear all fields
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

/**
 * We use oneOfType because
 * the application uses AppSyncClient
 * the tests/jest provide ApolloClient as we use the <MockProvider/>
 */
EnterDropDetails.propTypes = {
  graphClient: PropTypes.oneOfType([
    PropTypes.instanceOf(AWSAppSyncClient).isRequired,
    PropTypes.instanceOf(ApolloClient).isRequired,
  ]).isRequired,
};

// withClient will provide the client in props
export default withClient(EnterDropDetails);
